import React from "react";

import { UserRole } from "@modules/authentication/roles";
import { useFetchAll } from "~/hooks/useFetchAll";
import { useOrganisationsLazyQuery } from "generated/hooks";
import { useInjection } from "@di/DependencyContext";
import { DependencyType } from "@di/DependencyType";
import { UserService, type UserSortKey } from "@modules/user/user.service";
import { SelectionComboboxOption } from "~/components/ui/selection-combobox";
import { toUserViewModel, type UserViewModel } from "./userViewModel";

export const ALL_ROLES = "ALL_ROLES";
export const ALL_ORGANISATIONS = "ALL_ORGANISATIONS";
const SEARCH_DEBOUNCE_MS = 350;

export const ROLE_FILTER_OPTIONS: SelectionComboboxOption[] = [
  { value: ALL_ROLES, label: "All roles" },
  { value: UserRole.User, label: "User" },
  { value: UserRole.Admin, label: "Admin" },
  { value: UserRole.SuperAdmin, label: "SuperAdmin" },
];

const dedupeSelectionOptions = (options: SelectionComboboxOption[]) =>
  Array.from(new Map(options.map((option) => [option.value, option])).values());

/**
 * Owns the search, filter, sort and cursor-pagination state for the users table,
 * so every page that lists users shares one implementation.
 */
export const useUsersTable = () => {
  const [users, setUsers] = React.useState<UserViewModel[]>([]);
  const [isUsersLoading, setIsUsersLoading] = React.useState(true);
  const [hasNextPage, setHasNextPage] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageCursors, setPageCursors] = React.useState<
    Map<number, string | null>
  >(new Map([[0, null]]));
  const [expandedUserIds, setExpandedUserIds] = React.useState<Set<string>>(
    new Set()
  );
  const [searchTerm, setSearchTermState] = React.useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState("");
  const [selectedRole, setSelectedRoleState] = React.useState<
    UserRole | typeof ALL_ROLES
  >(ALL_ROLES);
  const [selectedOrganisationId, setSelectedOrganisationIdState] =
    React.useState(ALL_ORGANISATIONS);
  const [sortKey, setSortKey] = React.useState<UserSortKey>("fullName");
  const [sortDirection, setSortDirection] = React.useState<"ASC" | "DESC">(
    "ASC"
  );
  const [reloadToken, setReloadToken] = React.useState(0);

  const [fetchOrganisations] = useOrganisationsLazyQuery();
  const { data: allOrganisations } = useFetchAll(
    fetchOrganisations,
    (result) => result.organisations,
    { variables: { after: null } }
  );

  const userService = useInjection<UserService>(DependencyType.UserService);
  const currentPageCursor = pageCursors.get(page) ?? null;

  const organisationFilterOptions = React.useMemo<SelectionComboboxOption[]>(
    () =>
      dedupeSelectionOptions([
        { value: ALL_ORGANISATIONS, label: "All organisations" },
        ...(allOrganisations ?? [])
          .filter(
            (organisation): organisation is { id: string; name: string } =>
              Boolean(organisation?.id && organisation?.name)
          )
          .map((organisation) => ({
            value: organisation.id,
            label: organisation.name,
          }))
          .sort((left, right) => left.label.localeCompare(right.label)),
      ]),
    [allOrganisations]
  );

  const selectedRoleLabel =
    ROLE_FILTER_OPTIONS.find((option) => option.value === selectedRole)
      ?.label ?? "All roles";
  const selectedOrganisationLabel =
    organisationFilterOptions.find(
      (option) => option.value === selectedOrganisationId
    )?.label ?? "All organisations";

  const resetPagination = React.useCallback(() => {
    setPage(0);
    setPageCursors(new Map([[0, null]]));
    setExpandedUserIds(new Set());
  }, []);

  const setSearchTerm = React.useCallback(
    (value: string) => {
      setSearchTermState(value);
      resetPagination();
    },
    [resetPagination]
  );

  const setSelectedRole = React.useCallback(
    (value: UserRole | typeof ALL_ROLES) => {
      setSelectedRoleState(value);
      resetPagination();
    },
    [resetPagination]
  );

  const setSelectedOrganisationId = React.useCallback(
    (value: string) => {
      setSelectedOrganisationIdState(value);
      resetPagination();
    },
    [resetPagination]
  );

  const handleSort = React.useCallback(
    (key: UserSortKey) => {
      const nextDirection: "ASC" | "DESC" =
        sortKey === key && sortDirection === "ASC" ? "DESC" : "ASC";
      setSortKey(key);
      setSortDirection(nextDirection);
      resetPagination();
    },
    [sortKey, sortDirection, resetPagination]
  );

  const goToPreviousPage = React.useCallback(() => {
    setPage((currentPage) => (currentPage > 0 ? currentPage - 1 : currentPage));
  }, []);

  const goToNextPage = React.useCallback(() => {
    if (hasNextPage) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [hasNextPage]);

  /** Re-fetches the current page, e.g. after a mutation changed a row. */
  const reload = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const updateUser = React.useCallback(
    (userId: string, patch: Partial<UserViewModel>) => {
      setUsers((previous) =>
        previous.map((user) =>
          user.id === userId ? { ...user, ...patch } : user
        )
      );
    },
    []
  );

  const toggleExpandedUser = React.useCallback((userId: string) => {
    setExpandedUserIds((previous) => {
      const next = new Set(previous);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }

      return next;
    });
  }, []);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [searchTerm]);

  React.useEffect(() => {
    // Ignore responses from requests superseded by a newer filter, sort or page.
    let isActive = true;
    setIsUsersLoading(true);

    userService
      .getUsers({
        after: currentPageCursor,
        searchTerm: debouncedSearchTerm,
        role: selectedRole === ALL_ROLES ? null : selectedRole,
        organisationId:
          selectedOrganisationId === ALL_ORGANISATIONS
            ? null
            : selectedOrganisationId,
        sortKey,
        sortDirection,
      })
      .then((result) => {
        if (!isActive) {
          return;
        }

        const mappedUsers = result.users.map(toUserViewModel);

        setUsers(mappedUsers);
        setExpandedUserIds((previous) => {
          const remainingExpanded = mappedUsers
            .map((user) => user.id)
            .filter((id) => previous.has(id));

          return new Set(remainingExpanded);
        });
        setHasNextPage(result.hasNextPage);

        if (result.hasNextPage && result.endCursor) {
          setPageCursors((previous) => {
            if (previous.get(page + 1) === result.endCursor) {
              return previous;
            }

            const next = new Map(previous);
            next.set(page + 1, result.endCursor ?? null);
            return next;
          });
        }
      })
      .catch((error) => {
        if (!isActive) {
          return;
        }

        console.error(error);
        setUsers([]);
        setHasNextPage(false);
      })
      .finally(() => {
        if (isActive) {
          setIsUsersLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [
    page,
    currentPageCursor,
    userService,
    debouncedSearchTerm,
    selectedRole,
    selectedOrganisationId,
    sortKey,
    sortDirection,
    reloadToken,
  ]);

  return {
    users,
    isUsersLoading,
    page,
    hasNextPage,
    expandedUserIds,
    searchTerm,
    selectedRole,
    selectedRoleLabel,
    selectedOrganisationLabel,
    organisationFilterOptions,
    sortKey,
    sortDirection,
    setSearchTerm,
    setSelectedRole,
    setSelectedOrganisationId,
    handleSort,
    goToPreviousPage,
    goToNextPage,
    toggleExpandedUser,
    updateUser,
    reload,
  };
};

export type UsersTableState = ReturnType<typeof useUsersTable>;
